/**
 * Prepare inbound/outbound email HTML for in-app display.
 * Strips scripts and softens document-level tags so we can safely isolate in Shadow DOM.
 */
export function prepareTicketEmailHtmlForDisplay(html: string): string {
  if (!html.trim()) return html;

  return (
    html
      // Scripts must never run in the portal.
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<link\b[^>]*>/gi, '')
      .replace(/<meta\b[^>]*>/gi, '')
      .replace(/<base\b[^>]*>/gi, '')
      // Drop document wrappers — body styles are rewritten below / live in shadow.
      .replace(/<\/?(?:html|head)\b[^>]*>/gi, '')
      .replace(/<body\b([^>]*)>/gi, '<div class="ticket-mail__email-root"$1>')
      .replace(/<\/body>/gi, '</div>')
      // Neutralize inline handlers (onclick, etc.).
      .replace(/\son[a-z]+\s*=\s*(['"])[\s\S]*?\1/gi, '')
      .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
  );
}

/** Rewrite global email CSS selectors so they only apply under the given scope. */
export function scopeEmailCss(css: string, scope: string): string {
  const cleaned = css
    .replace(/@import[^;]+;/gi, '')
    .replace(/<\/?style\b[^>]*>/gi, '')
    .trim();
  if (!cleaned) return '';

  // Split at top-level braces while keeping @media / @supports blocks intact enough
  // for typical marketing-email stylesheets.
  const parts: string[] = [];
  let buf = '';
  let depth = 0;
  for (let i = 0; i < cleaned.length; i += 1) {
    const ch = cleaned[i]!;
    buf += ch;
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        parts.push(buf.trim());
        buf = '';
      }
    }
  }
  if (buf.trim()) parts.push(buf.trim());

  return parts
    .map((block) => {
      const open = block.indexOf('{');
      if (open < 0) return '';
      const selectors = block.slice(0, open).trim();
      let body = block.slice(open);
      if (!selectors) return '';

      if (selectors.startsWith('@')) {
        // @media / @supports / @keyframes — scope nested rule lists when possible.
        const inner = body.slice(1, body.lastIndexOf('}'));
        if (/^@(?:media|supports)\b/i.test(selectors)) {
          const scopedInner = scopeEmailCss(inner, scope);
          return scopedInner ? `${selectors}{${scopedInner}}` : '';
        }
        // Leave @keyframes / @font-face as-is (font-face is shadow-local when in shadow).
        return block;
      }

      const scopedSelectors = selectors
        .split(',')
        .map((sel) => {
          let s = sel.trim();
          if (!s) return '';
          s = s
            .replace(/^html\b/i, scope)
            .replace(/^:root\b/i, scope)
            .replace(/^body\b/i, scope);
          if (
            s === scope ||
            s.startsWith(`${scope} `) ||
            s.startsWith(`${scope}.`) ||
            s.startsWith(`${scope}:`) ||
            s.startsWith(`${scope}[`)
          ) {
            return s;
          }
          return `${scope} ${s}`;
        })
        .filter(Boolean)
        .join(', ');

      // body/html → :host must not create a nested scrollport in the ticket pane.
      const touchesHostOnly = scopedSelectors
        .split(',')
        .every((s) => {
          const t = s.trim();
          return t === scope;
        });
      if (touchesHostOnly) {
        const decls = body.slice(1, body.lastIndexOf('}'));
        const cleanedDecls = decls
          .replace(/(?:^|;)\s*overflow(?:-[xy])?\s*:[^;{]+/gi, ';')
          .replace(/(?:^|;)\s*(?:max-|min-)?height\s*:[^;{]+/gi, ';')
          .replace(/;;+/g, ';')
          .replace(/^;|;$/g, '')
          .trim();
        if (!cleanedDecls) return '';
        body = `{${cleanedDecls}}`;
      }

      return scopedSelectors ? `${scopedSelectors}${body}` : '';
    })
    .filter(Boolean)
    .join('\n');
}

/** Scope every &lt;style&gt; block in email HTML to `scope` (for non-shadow fallback). */
export function scopeTicketEmailStyles(html: string, scope = '.ticket-mail__body'): string {
  return html.replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (_m, css: string) => {
    const scoped = scopeEmailCss(css, scope);
    return scoped ? `<style>${scoped}</style>` : '';
  });
}
